// Runs in the browser before React hydration. Captures client-side errors,
// including those surfaced from the WebContainer preview/editor.
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/admin/projects/:id\",\"regex\":\"^/admin/projects/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/invite/:token\",\"regex\":\"^/invite/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:id\",\"regex\":\"^/project/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:id/messages\",\"regex\":\"^/project/([^/]+)/messages$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/survey/:surveyId\",\"regex\":\"^/survey/([^/]+)$\",\"paramNames\":[\"surveyId\"],\"hasOptionalPrefix\":false},{\"path\":\"/templates/:slug\",\"regex\":\"^/templates/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/forgot-password\"},{\"path\":\"/login\"},{\"path\":\"/privacy\"},{\"path\":\"/refund\"},{\"path\":\"/signup\"},{\"path\":\"/terms\"},{\"path\":\"/admin/builds\"},{\"path\":\"/admin/payment-requests\"},{\"path\":\"/admin/prompts\"},{\"path\":\"/admin/users\"},{\"path\":\"/affiliates\"},{\"path\":\"/auth/update-password\"},{\"path\":\"/billing/credits\"},{\"path\":\"/billing-preview\"},{\"path\":\"/checkout\"},{\"path\":\"/checkout/return\"},{\"path\":\"/community\"},{\"path\":\"/debug/error-tracking\"},{\"path\":\"/dev/grow-v1\"},{\"path\":\"/dev/grow-v1-workspace\"},{\"path\":\"/growth/billing\"},{\"path\":\"/growth/settings\"},{\"path\":\"/pricing\"},{\"path\":\"/profile\"},{\"path\":\"/projects\"},{\"path\":\"/referrals\"},{\"path\":\"/settings\"},{\"path\":\"/settings/account\"},{\"path\":\"/settings/billing\"},{\"path\":\"/settings/invite-friends\"},{\"path\":\"/settings/notifications\"},{\"path\":\"/settings/security\"},{\"path\":\"/settings/usage\"},{\"path\":\"/start\"},{\"path\":\"/templates\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.3";import * as Sentry from "@sentry/nextjs";
import { isInAppBrowserBridgeNoise } from "@/lib/observability/in-app-browser-noise";
import {
  classifyErrorOrigin,
  GENERATED_APP_PREVIEW_ORIGIN,
} from "@/lib/observability/error-origin";
import {
  isExternalDomMutationNoise,
  DOM_MUTATION_EXTERNAL_ORIGIN,
} from "@/lib/observability/dom-mutation-noise";
import {
  isExternalCrossOriginFrameNoise,
  CROSS_ORIGIN_FRAME_EXTERNAL_ORIGIN,
} from "@/lib/observability/cross-origin-frame-noise";
import {
  isEmbeddedCrawlerNoise,
  EMBEDDED_CRAWLER_ORIGIN,
} from "@/lib/observability/embedded-crawler-noise";
import { installDomMutationGuard } from "@/lib/observability/dom-mutation-guard";
import { captureFbcCookie } from "@/lib/marketing/fbc-cookie";
import { routeTagFor } from "@/lib/observability/route-tag";

// Harden the two DOM operations React uses to commit, BEFORE hydration, so a
// browser translation tool rewriting the page's text nodes can't white-screen
// the shell with a NotFoundError (facebook/react#11538). This file runs in the
// browser before the app's frontend code, which is exactly where the patch
// must land. The residual Sentry reports are dropped in beforeSend below.
installDomMutationGuard();

// Plant the `_fbc` attribution cookie before any `afterInteractive` tag runs.
// Lives here — NOT as an inline <head> script — because a raw script child of
// the React-owned <head> is a positional hydration anchor that Meta in-app
// browsers break by injecting their own scripts (see lib/marketing/fbc-cookie).
captureFbcCookie();

// The route the event was captured on. Prefer Sentry's own request URL (stamped
// at capture time); fall back to the live location. Lets EVERY error — DOM
// mutation or otherwise — identify the area it mounted in (acceptance criteria).
function currentRouteTag(event: { request?: { url?: string } }): string | undefined {
  const url = event.request?.url;
  if (typeof url === "string") {
    try {
      const tag = routeTagFor(new URL(url).pathname);
      if (tag) return tag;
    } catch {
      // malformed URL — fall through to window.location
    }
  }
  if (typeof window !== "undefined") {
    return routeTagFor(window.location?.pathname);
  }
  return undefined;
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.NODE_ENV,
  // Keep tracing low during the private beta to control event volume/cost.
  tracesSampleRate: 0.1,
  // Drop known browser/iframe junk so real bugs don't get buried (and we don't
  // burn quota on noise). These are benign and not actionable.
  ignoreErrors: [
    // Fired by layout libraries; harmless and extremely noisy.
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    // Transient network blips and user-cancelled requests.
    "Failed to fetch",
    "NetworkError when attempting to fetch resource",
    "Load failed",
    // Errors thrown by browser extensions, not our code.
    /extensions\//i,
    /^chrome-extension:\/\//i,
    // Mobile in-app-browser (Facebook/Instagram) native-bridge beacon that
    // throws on unload. Message-level catch; beforeSend also matches on the
    // `iabjs://` stack source for shells that word the message differently.
    /Error invoking postMessage: Java exception was raised during method invocation/i,
  ],
  // Stack-source catch-all for in-app-browser native-bridge errors: they
  // originate in the shell's injected `iabjs://` script, not Panda code, and do
  // not break the user flow, so they must not be grouped as Panda crashes.
  beforeSend(event) {
    if (isInAppBrowserBridgeNoise(event)) return null;

    // Browser translation tools rewrite the page's text nodes, invalidating the
    // child references React holds; its next removeChild/insertBefore then
    // throws NotFoundError from deep in react-dom with no Panda frame owning the
    // mutation. Not a Panda bug and not actionable — the runtime guard already
    // stops it crashing the shell — so drop it instead of letting it file as a
    // shell crash (JAVASCRIPT-NEXTJS-1F / 17).
    if (isExternalDomMutationNoise(event)) {
      event.tags = { ...event.tags, error_origin: DOM_MUTATION_EXTERNAL_ORIGIN };
      return null;
    }

    // A `SecurityError` from reading a restricted property off a cross-origin
    // frame's Window, with no Panda frame owning the access. Panda touches the
    // preview iframe only via `postMessage` (always permitted) and the element's
    // own `contentWindow`/`contentDocument` getters (never throw), so these come
    // from browser extensions or the page's third-party scripts — GTM, which
    // embeds its own cross-origin iframe, GA, the Meta pixel, PostHog's rrweb
    // recorder. Not a Panda bug and not actionable, so drop it instead of
    // letting it file as a shell crash (JAVASCRIPT-NEXTJS-F). The predicate
    // keeps any event a `/_next/static/` frame of OURS owns, so a genuine
    // cross-origin regression in our iframe handling still reports.
    if (isExternalCrossOriginFrameNoise(event)) {
      event.tags = { ...event.tags, error_origin: CROSS_ORIGIN_FRAME_EXTERNAL_ORIGIN };
      return null;
    }

    // A CefSharp host-object bridge rejection — "Object Not Found Matching
    // Id:N, MethodName:update, ParamCount:4" — raised inside an embedded
    // browser crawler (Microsoft Outlook Safe Links and other enterprise link
    // scanners) as it pre-renders a shared /project/:id link. `update` is
    // CefSharp's bound-object method, not a Panda update call; Panda registers
    // no CefSharp host objects, so no real user session produces it and no
    // Panda code path can prevent it. Drop it instead of letting bot traffic
    // file as a shell crash (JAVASCRIPT-NEXTJS-5).
    if (isEmbeddedCrawlerNoise(event)) {
      event.tags = { ...event.tags, error_origin: EMBEDDED_CRAWLER_ORIGIN };
      return null;
    }

    // Route tag on every surviving event so future errors identify the area
    // they mounted in (the error boundaries add a more precise route context).
    const route = currentRouteTag(event);
    if (route) event.tags = { ...event.tags, route };

    // Tag every event with where it actually came from — Panda's own workspace
    // shell, or the GENERATED app running inside the cross-origin preview
    // sandbox (E2B / legacy WebContainer). A React crash in a user's generated
    // app (e.g. "Rendered more hooks than during the previous render", a classic
    // LLM hooks-order mistake) surfaces on the /project/:id route but is NOT a
    // Panda bug — the shell render tree is provably free of hooks-order
    // violations. Without this, those get filed as platform crashes
    // (JAVASCRIPT-NEXTJS-1H) and drown the real shell signal.
    const origin = classifyErrorOrigin(event);
    event.tags = { ...event.tags, error_origin: origin };
    if (origin === GENERATED_APP_PREVIEW_ORIGIN) {
      // Group generated-app crashes on their own, never merged into a shell
      // issue. `{{ default }}` keeps Sentry's usual per-crash grouping WITHIN
      // this bucket, so distinct app bugs stay distinct — they just never mix
      // with Panda's own errors.
      event.fingerprint = [GENERATED_APP_PREVIEW_ORIGIN, "{{ default }}"];
    }
    return event;
  },
});

// Adds navigation breadcrumbs so an error shows what the user did beforehand.
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
